import {
	Button,
	FormControl,
	FormHelperText,
	Grid2,
	TextField,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import {
	Controller,
	type SubmitHandler,
	useForm,
	useWatch,
} from "react-hook-form";
import { ErrorModal } from "../../../../components/modals/error-modal";
import MesfModal from "../../../../components/modals/modal.mesf";
import { ButtonWithLoading } from "../../../../components/shared/buttons/button-with-loading";
import { ConfirmationDialogRaw } from "../../../../controls/modals/ConfirmationDialogRaw";
import { NotificationSnackBar } from "../../../../controls/snackbars/notification-snackbar";
import { HelmetDexteel } from "../../../../helmet/HelmetDexteel";
import type { EmailAddressTopic } from "../models/EmailAddressTopic";
import type { EmailTopic } from "../models/EmailTopic";
import {
	type EmailTopicFormValues,
	INITIAL_VALUES,
	type RecipientField,
} from "../models/FormInitialValues";
import { upsertEmailTopic } from "../repositories/EmailTopicsRepository";
import { RecipientsTable } from "./RecipientsTable";

type Props = {
	modal: "" | "create" | "update";
	onHide: (shouldUpdate: boolean) => void;
	topic: EmailTopic | null;
	assignedAddresses: EmailAddressTopic[];
	includeSystem: boolean;
};

export const UpsertDeleteModal = ({
	modal,
	onHide,
	topic,
	assignedAddresses,
	includeSystem,
}: Props) => {
	const queryClient = useQueryClient();

	const [message, setMessage] = useState("");
	const [error, setError] = useState("");
	const [pendingAddress, setPendingAddress] = useState<string | null>(null);
	const [pendingAction, setPendingAction] = useState<"cancel" | "save" | null>(
		null,
	);

	const { control, getValues, setValue, reset, handleSubmit } =
		useForm<EmailTopicFormValues>({
			defaultValues: INITIAL_VALUES,
		});

	const selectedTO = useWatch({ control, name: "AddressIdsTO" });
	const selectedCC = useWatch({ control, name: "AddressIdsCC" });
	const selectedBCC = useWatch({ control, name: "AddressIdsBCC" });

	const selectedByField: Record<RecipientField, number[]> = {
		AddressIdsTO: selectedTO ?? [],
		AddressIdsCC: selectedCC ?? [],
		AddressIdsBCC: selectedBCC ?? [],
	};

	const toggleAddress = (
		field: RecipientField,
		emailAddressId: number,
		checked: boolean,
	) => {
		const current = getValues(field) ?? [];

		setValue(
			field,
			checked
				? [...current, emailAddressId]
				: current.filter((id) => id !== emailAddressId),
			{ shouldDirty: true },
		);
	};

	const upsertTopicMutation = useMutation({
		mutationFn: async (data: EmailTopicFormValues) => {
			const trimmedDelay = data.DelayTimeMinutes.trim();

			await upsertEmailTopic({
				EmailTopicId: data.EmailTopicId,
				EmailTopicName: data.EmailTopicName,
				Description: data.Description.trim() || null,
				DelayTimeMinutes: trimmedDelay === "" ? null : Number(trimmedDelay),
				IsActive: data.IsActive,
				AddressIdsTO: data.AddressIdsTO,
				AddressIdsCC: data.AddressIdsCC,
				AddressIdsBCC: data.AddressIdsBCC,
			});
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["emailTopics"] });
			setMessage(
				`The email topic was ${modal === "update" ? "updated" : modal}d succesfully`,
			);
			onHide(true);
		},
		onError: (mutationError: Error) => {
			setError(mutationError.message);
		},
	});

	const onSubmit: SubmitHandler<EmailTopicFormValues> = async (data) => {
		if (pendingAddress) {
			setPendingAction("save");
			return;
		}

		await upsertTopicMutation.mutateAsync(data);
	};

	const onCancel = () => {
		if (pendingAddress) {
			setPendingAction("cancel");
			return;
		}

		onHide(false);
	};

	const onPendingActionConfirmationClose = async (value?: string) => {
		const action = pendingAction;

		setPendingAction(null);

		if (value !== "OK") {
			return;
		}

		if (action === "cancel") {
			onHide(false);
			return;
		}

		await upsertTopicMutation.mutateAsync(getValues());
	};

	useEffect(() => {
		if (modal === "create" || !topic) {
			reset(INITIAL_VALUES);
			return;
		}

		const idsOfType = (recipientType: string) =>
			assignedAddresses
				.filter((address) => address.RecipientType === recipientType)
				.map((address) => address.EmailAddressId);

		reset({
			EmailTopicId: topic.EmailTopicId,
			EmailTopicName: topic.EmailTopicName,
			Description: topic.Description ?? "",
			DelayTimeMinutes:
				topic.DelayTimeMinutes == null ? "" : String(topic.DelayTimeMinutes),
			IsActive: topic.IsActive,
			AddressIdsTO: idsOfType("TO"),
			AddressIdsCC: idsOfType("CC"),
			AddressIdsBCC: idsOfType("BCC"),
		});
	}, [assignedAddresses, modal, reset, topic]);

	const isTopicBusy = upsertTopicMutation.isPending;

	return (
		<>
			{modal !== "" ? (
				<HelmetDexteel
					title={`${
						modal == "create" ? "New" : modal == "update" ? "Edit" : ""
					} Email Topics`}
				/>
			) : null}
			<MesfModal
				title={`${modal == "update" ? "EDIT" : "CREATE"} EMAIL TOPICS`}
				open={modal !== ""}
				handleClose={onCancel}
				maxWidth="md"
				id="email-topic-modal"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content dividers style={{ padding: "10px 30px" }}>
						<Grid2 container spacing={1}>
							<Grid2 size={{ xs: 12, sm: 8 }}>
								<Controller
									name="EmailTopicName"
									control={control}
									rules={{
										required: "Name is required",
										validate: (value) =>
											value.trim().length > 0 || "Name is required",
									}}
									render={({
										field: { onChange, value },
										fieldState: { error: fieldError },
									}) => (
										<FormControl error={!!fieldError} fullWidth>
											<TextField
												label="Name"
												variant="outlined"
												fullWidth
												margin="dense"
												autoComplete="off"
												value={value}
												disabled={modal !== "create" || isTopicBusy}
												onChange={onChange}
											/>
											{fieldError ? (
												<FormHelperText>{fieldError.message}</FormHelperText>
											) : null}
										</FormControl>
									)}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, sm: 4 }}>
								<Controller
									name="DelayTimeMinutes"
									control={control}
									rules={{
										validate: (value) => {
											const trimmed = value.trim();

											if (trimmed === "") {
												return true;
											}

											return (
												(/^\d+$/.test(trimmed) && Number(trimmed) >= 0) ||
												"Delay must be a positive whole number"
											);
										},
									}}
									render={({
										field: { onChange, value },
										fieldState: { error: fieldError },
									}) => (
										<FormControl error={!!fieldError} fullWidth>
											<TextField
												label="Delay (min)"
												variant="outlined"
												fullWidth
												margin="dense"
												autoComplete="off"
												value={value}
												disabled={isTopicBusy}
												onChange={onChange}
											/>
											{fieldError ? (
												<FormHelperText>{fieldError.message}</FormHelperText>
											) : null}
										</FormControl>
									)}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12 }}>
								<Controller
									name="Description"
									control={control}
									render={({ field: { onChange, value } }) => (
										<TextField
											label="Description"
											variant="outlined"
											fullWidth
											multiline
											rows={2}
											margin="dense"
											autoComplete="off"
											value={value}
											disabled={isTopicBusy}
											onChange={onChange}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12 }}>
								<RecipientsTable
									selectedByField={selectedByField}
									onToggle={toggleAddress}
									onError={setError}
									onPendingAddressChange={setPendingAddress}
									includeSystem={includeSystem}
									disabled={isTopicBusy}
									enabled={modal !== ""}
								/>
							</Grid2>
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<Grid2 container spacing={2} justifyContent="flex-end">
							<Grid2 size={{ md: 3, xs: 12 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={onCancel}
								>
									Cancel
								</Button>
							</Grid2>
							<Grid2 size={{ md: 3, xs: 12 }}>
								<ButtonWithLoading
									fullWidth
									isLoading={isTopicBusy}
									variant="contained"
									color="primary"
									type="submit"
								>
									Save
								</ButtonWithLoading>
							</Grid2>
						</Grid2>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<ConfirmationDialogRaw
				open={pendingAction !== null}
				onClose={onPendingActionConfirmationClose}
				title="Unsaved Address"
				suffixTitle="Email Topics"
				message={
					<>
						You are in the middle of adding the email address{" "}
						<b>{pendingAddress}</b> and it has not been saved yet.
						<br />
						<br />
						Do you want to continue and discard it?
					</>
				}
			/>
			<NotificationSnackBar message={message} onHide={() => setMessage("")} />
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error In Email Topics"
			/>
		</>
	);
};
